import React, { useRef, useCallback, useState, useEffect } from "react";
import { Dropdown, Icon, IconButton, ResponsiveMode, DirectionalHint } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { PET_COLORMAPS } from "../../../helpers/ColorLUTService";
import "../../../styles/ColorLUTTool.css";

const DEBOUNCE_DELAY = 1000;

const FUSION_NONE_KEY = "__none__";

const defaultColormapOptions = [
  { key: FUSION_NONE_KEY, text: "None" },
  ...Object.entries(PET_COLORMAPS).map(([key, value]) => ({ key: value, text: key })),
];

const dropdownStyles = {
  title: {
    backgroundColor: "rgba(30, 30, 30, 0.9)",
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    fontSize: 12,
    height: 28,
    lineHeight: "26px",
    borderRadius: 4,
  },
  caretDownWrapper: { color: "#fff", lineHeight: "26px" },
  caretDown: { color: "#fff" },
  dropdown: { background: "transparent" },
};

/**
 * Overlay rendered at the bottom of the active viewport via portal.
 * Shows colormap dropdown + opacity slider when fusion/color LUT is active.
 * Draggable via the gripper handle on the left.
 */
const ColorLUTOverlay = ({
  fusionColormap,
  fusionOpacity,
  onColormapChange,
  onOpacityChange,
  onReset,
  colormapOptionsList,
  resetTitle,
}) => {
  const { t } = useTranslation();
  const opacityTimer = useRef(null);
  const panelRef = useRef(null);
  const dragOrigin = useRef(null);
  const [dragged, setDragged] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  //  use POINTER events so "Drag to reposition" works on touch too (mouse events
  // never fire on a touch device). Pointer events cover mouse, touch and pen.
  const handleDragStart = useCallback((e) => {
    if (e.button != null && e.button > 0) return; // ignore non-primary buttons (touch = 0)
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const container = panel.parentElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();

    dragOrigin.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      panelTop: rect.top - containerRect.top,
      panelLeft: rect.left - containerRect.left,
    };
    e.preventDefault();
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const onPointerMove = (e) => {
      if (!dragOrigin.current || !panelRef.current) return;
      const panel = panelRef.current;
      const container = panel.parentElement;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();

      const dx = e.clientX - dragOrigin.current.mouseX;
      const dy = e.clientY - dragOrigin.current.mouseY;

      const newTop = dragOrigin.current.panelTop + dy;
      const newLeft = dragOrigin.current.panelLeft + dx;

      const maxLeft = containerRect.width - panel.offsetWidth;
      const maxTop = containerRect.height - panel.offsetHeight;

      setPos({
        top: Math.max(0, Math.min(newTop, maxTop)),
        left: Math.max(0, Math.min(newLeft, maxLeft)),
      });
      setDragged(true);
      e.preventDefault();
    };

    const onPointerUp = () => {
      dragOrigin.current = null;
    };

    // passive:false so preventDefault() can stop the browser treating the touch-drag as a scroll.
    document.addEventListener("pointermove", onPointerMove, { passive: false });
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
    return () => {
      document.removeEventListener("pointermove", onPointerMove, { passive: false });
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, []);

  const handleColormapChange = useCallback(
    (e, option) => onColormapChange(option.key),
    [onColormapChange]
  );

  const handleOpacityChange = useCallback(
    (e) => {
      const value = parseFloat(e.target.value);
      clearTimeout(opacityTimer.current);
      opacityTimer.current = setTimeout(() => onOpacityChange(value), DEBOUNCE_DELAY);
    },
    [onOpacityChange]
  );

  const options = colormapOptionsList || defaultColormapOptions;
  const positionStyle = dragged
    ? { top: pos.top, left: pos.left, bottom: "auto", transform: "none" }
    : {};

  return (
    <div
      ref={panelRef}
      // on mobile add the `--mobile` modifier so the overlay stacks into
      // two lines (dropdown on top, drag+slider+close below) to fit a narrow multi-viewport layout.
      className={`fusion-viewport-overlay${isMobile ? " fusion-viewport-overlay--mobile" : ""}`}
      style={positionStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="fusion-overlay-row">
        <div
          className="fusion-overlay-drag-handle"
          // pointer event + touch-action:none so drag works on touch, not just mouse.
          onPointerDown={handleDragStart}
          style={{ touchAction: "none" }}
          title={t("viewer.tools.fusion-drag-hint", "Drag to reposition")}
        >
          <Icon iconName="GripperDotsVertical" className="fusion-overlay-gripper-icon" />
        </div>

        <Dropdown
          options={options}
          selectedKey={fusionColormap}
          onChange={handleColormapChange}
          className="fusion-overlay-dropdown"
          dropdownWidth={160}
          styles={dropdownStyles}
            //  Fluent otherwise switches to a full-screen Panel on small
          // screens; force it to stay a compact callout and open it ABOVE the field (the overlay
          // sits at the bottom of the viewport), like the other mobile viewer dropdowns.
          responsiveMode={ResponsiveMode.large}
          calloutProps={{
            directionalHint: DirectionalHint.topLeftEdge,
            directionalHintFixed: true,
          }}
          // Caret points up on mobile since the menu opens upward.
          onRenderCaretDown={
            isMobile
              ? () => <Icon iconName="ChevronUp" className="fusion-overlay-caret" />
              : undefined
          }
        />

        <div className="fusion-overlay-slider-group">
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            defaultValue={fusionOpacity}
            onChange={handleOpacityChange}
            className="fusion-overlay-slider"
          />
          <span className="fusion-overlay-value">
            {Math.round(fusionOpacity * 100)}%
          </span>
        </div>

        <IconButton
          iconProps={{ iconName: "Cancel" }}
          title={resetTitle || t("viewer.tools.fusion-remove", "Remove Fusion")}
          className="fusion-overlay-reset-btn"
          onClick={onReset}
        />
      </div>
    </div>
  );
};

export default ColorLUTOverlay;
